/**
 * SSE client for `GET /v1/me/stream` (docs/08 "Realtime"). EventSource cannot send the bearer
 * token, so this reads the stream with fetch, parses `text/event-stream` itself, and reconnects
 * with backoff sending `Last-Event-ID` (the last id seen, pings included), so a serverless
 * function ending the stream loses nothing. Messages invalidate TanStack Query keys by module.
 */
import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export type SseMessage = { id?: string; event: string; data: unknown };

/** Parse complete events from a buffer; returns the unparsed tail. */
export function parseSse(buffer: string): { messages: SseMessage[]; rest: string } {
  const messages: SseMessage[] = [];
  const normalised = buffer.replace(/\r\n/g, '\n');
  const blocks = normalised.split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    let id: string | undefined;
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
      if (field === 'id') id = value;
      else if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length === 0 && id === undefined) continue;
    let parsed: unknown = data.join('\n');
    try {
      parsed = JSON.parse(parsed as string);
    } catch {
      // plain text payload
    }
    messages.push({ ...(id !== undefined ? { id } : {}), event, data: parsed });
  }
  return { messages, rest };
}

export type StreamOptions = {
  url: string;
  getToken(): string | null;
  getOrgId(): string | null;
  onMessage(message: SseMessage): void;
  onStatus?(status: 'open' | 'reconnecting' | 'closed'): void;
  fetchImpl?: typeof fetch;
  /** Backoff in ms for attempt n (default 1 s doubling to 30 s). */
  backoff?(attempt: number): number;
  /** Initial Last-Event-ID (e.g. persisted). */
  lastEventId?: string | null;
};

/** Start the stream; returns `stop()`. */
export function connectStream(options: StreamOptions): () => void {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const backoff = options.backoff ?? ((n: number) => Math.min(30_000, 1000 * 2 ** n));
  let lastId: string | null = options.lastEventId ?? null;
  let stopped = false;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;

  const schedule = () => {
    if (stopped) return;
    options.onStatus?.('reconnecting');
    timer = setTimeout(run, backoff(attempt));
    attempt += 1;
  };

  async function run(): Promise<void> {
    if (stopped) return;
    const token = options.getToken();
    if (!token) return schedule();
    controller = new AbortController();
    const headers: Record<string, string> = { accept: 'text/event-stream', authorization: `Bearer ${token}` };
    const orgId = options.getOrgId();
    if (orgId) headers['x-org-id'] = orgId;
    if (lastId) headers['last-event-id'] = lastId;
    try {
      const res = await doFetch(options.url, { headers, signal: controller.signal });
      if (!res.ok || !res.body) {
        if (res.status === 401) {
          options.onStatus?.('closed');
          return;
        }
        return schedule();
      }
      attempt = 0;
      options.onStatus?.('open');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { messages, rest } = parseSse(buffer);
        buffer = rest;
        for (const m of messages) {
          if (m.id !== undefined) lastId = m.id;
          if (m.event !== 'ping') options.onMessage(m);
        }
      }
      schedule(); // the server ended the stream (e.g. serverless max duration): reconnect
    } catch (error) {
      if (stopped || (error as { name?: string }).name === 'AbortError') return;
      schedule();
    }
  }

  void run();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    controller?.abort();
    options.onStatus?.('closed');
  };
}

/** What a stream message invalidates: `notification` → notifications; `entity-changed` → its module. */
export function invalidateFor(queryClient: QueryClient, message: SseMessage): void {
  if (message.event === 'notification') {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    return;
  }
  if (message.event === 'entity-changed') {
    const module = (message.data as { module?: string } | null)?.module;
    if (module) void queryClient.invalidateQueries({ queryKey: [module] });
    return;
  }
  if (message.event === 'message' || message.event === 'thread') {
    void queryClient.invalidateQueries({ queryKey: ['messaging'] });
  }
}

/** Keep one stream open while signed in; re-open when the token or org changes. */
export function useSseInvalidation(
  queryClient: QueryClient,
  options: Omit<StreamOptions, 'onMessage'> & { enabled: boolean; onMessage?(m: SseMessage): void },
): void {
  const { enabled, url, getToken, getOrgId, fetchImpl, backoff, onStatus, onMessage } = options;
  const token = enabled ? getToken() : null;
  const orgId = enabled ? getOrgId() : null;
  useEffect(() => {
    if (!enabled || !token) return;
    return connectStream({
      url,
      getToken,
      getOrgId,
      ...(fetchImpl ? { fetchImpl } : {}),
      ...(backoff ? { backoff } : {}),
      ...(onStatus ? { onStatus } : {}),
      onMessage: (m) => {
        invalidateFor(queryClient, m);
        onMessage?.(m);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect only on identity changes
  }, [enabled, token, orgId, url, queryClient]);
}
